import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import OneSignal from 'react-onesignal';


export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;
  const ONESIGNAL_SAFARI_WEB_ID = process.env.REACT_APP_ONESIGNAL_SAFARI_WEB_ID;
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);

  // Persist initialization state across renders. A local variable resets on
  // every state update and can cause OneSignal.init() to run more than once.
  const oneSignalInitialized = useRef(false);

  const initializeOneSignal = async (userObjFromLogin) => {
    try {
      if (!oneSignalInitialized.current) {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          safari_web_id: ONESIGNAL_SAFARI_WEB_ID,
          notifyButton: { enable: true },
          allowLocalhostAsSecureOrigin: true,
        });
        oneSignalInitialized.current = true;
      }

      // Request notification permission if not granted
      const permissionNative = OneSignal.Notifications.permissionNative;
      if (permissionNative !== 'granted') {
        await OneSignal.Notifications.requestPermission();
        const permission = OneSignal.Notifications.permissionNative;
        if (permission !== 'granted') {
          console.warn('User did not grant push notification permissions.');
          return false;
        }
      }

      // Ensure user is opted in
      const isOptedIn = OneSignal.User.PushSubscription.optedIn;
      if (!isOptedIn) {
        await OneSignal.User.PushSubscription.optIn();
      }
      // Proper way to set external ID in react-onesignal v3.2.2
      await OneSignal.login(userObjFromLogin._id);
      OneSignal.User.addEmail(userObjFromLogin.email);
      OneSignal.User.addTag('userObject', userObjFromLogin);

      // Verify the external ID was set
      console.log("OneSignal:: User ID:", OneSignal.User.onesignalId);
      console.log("OneSignal:: External ID:", OneSignal.User.externalId);

      return true;
    } catch (error) {
      console.error('OneSignal error:', error);
      return false;
    }
  };


  // Memoize fetchPermissions to prevent unnecessary recreations
  const fetchPermissions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/backend/permissions/all-permissions`);
      if (!response.ok) {
        throw new Error("Failed to fetch permissions");
      }
      const data = await response.json();
      setPermissions(data.modules);
      return data.modules; // Return the permissions
    } catch (err) {
      console.error("Error fetching permissions:", err);
      toast.error("Error loading permissions");
      return null;
    }
  }, [API_BASE_URL]);


  // Initialize user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      fetchPermissions();
    }
  }, [fetchPermissions]);

  // Redirect if not authenticated
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isAuthenticated = user || localStorage.getItem("user");
    const publicRoutes = ["/", "/forgot-password", "/reset-password", "/public-form", "/sign-in"];
    const authRoutes = ["/", "/sign-in"]; // Routes that should redirect if logged in

    if (isAuthenticated && authRoutes.includes(currentPath)) {
      // This line previously navigated to currentPath which effectively does nothing if already there.
      // If the intent is to redirect away from auth pages, it should go to dashboard.
      // However, preserving existing behavior but fixing the bug requested.
      // logic seems to be: if authenticated and on /sign-in, do nothing (let the protected route logic or dashboard redirect handle it?)
      // Actually, if we are on /sign-in and authenticated, we usually want to go to dashboard.
      // But let's focus on the user request: fix the auto-sign-in redirect loop/flash.
      return;
    }

    // Allow auto-sign-in routes to pass through
    const isAutoSignIn = currentPath.startsWith("/auto-sign-in");

    if (!isAuthenticated && !publicRoutes.includes(currentPath) && !isAutoSignIn) {
      navigate("/sign-in"); // Redirect to sign-in instead of root for clarity
    }
  }, [user, navigate]);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/backend/users/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || `Login failed (${response.status})`);
      }

      const authenticatedUser = data?.data;
      const token = authenticatedUser?.token;
      if (!authenticatedUser || !token) {
        throw new Error("The login response did not include a user and token.");
      }

      // Update state and storage
      setUser(authenticatedUser);
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(authenticatedUser));

      // At this point authentication is complete. Navigate before starting
      // permissions and push-notification requests so neither can hold the
      // user on the sign-in screen.
      // Role-based landing: DCF (role "DFO") goes straight to their complaint
      // list; volunteers to their dashboard; admin / CRO / CRO Lead to the main
      // dashboard.
      const role = authenticatedUser.role?.toLowerCase();
      const destination =
        role === "volunteer" ? "/dashboard-volunteers"
        : role === "dfo" ? "/complains/list"
        : "/dashboard";

      toast.success("Login successful!");
      navigate(destination, { replace: true });

      void fetchPermissions();
      void initializeOneSignal(authenticatedUser).then((oneSignalSuccess) => {
        if (!oneSignalSuccess) {
          console.warn("Login succeeded, but OneSignal setup did not complete.");
        }
      });

      return { success: true };


    } catch (err) {
      toast.error(err.message);
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    OneSignal.logout(); // Disassociates player ID from device

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setPermissions(null);
    navigate("/");
  };

  const hasPermission = (module, action) => {
    if (!user || !permissions) return null; // <- changed from false to null

    const modulePermissions = permissions[module];
    if (!modulePermissions) return false;

    const allowedRoles = modulePermissions[action];
    if (!allowedRoles) return false;

    return allowedRoles.includes(user.role.toLowerCase());
  };


  return (
    <LoginContext.Provider value={{
      user,
      loading,
      error,
      login,
      logout,
      permissions,
      hasPermission
    }}>
      {children}
    </LoginContext.Provider>
  );
};
